import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Shield, Star, Loader2, User, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import AuthModal from "./AuthModal";
import { trackEvent } from "@/lib/analytics";

export type PaywallContext =
  | "generic"
  | "analysis"
  | "strategy"
  | "content";

const CONTEXT_COPY: Record<PaywallContext, { headline: string; body: string }> = {
  generic: {
    headline: "Out of credits",
    body: "Add credits to your balance to continue analyzing messages, unlocking response strategy, and generating content.",
  },
  analysis: {
    headline: "You need 1 credit to run a full analysis",
    body: "Purchase a credit bundle to continue decoding messages.",
  },
  strategy: {
    headline: "You need 1 credit to unlock response strategy",
    body: "Purchase a credit bundle to keep generating neutralized responses.",
  },
  content: {
    headline: "You need 2 credits to generate content assets",
    body: "Purchase a credit bundle to create shareable content from your analyses.",
  },
};

interface PaywallModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context?: PaywallContext;
}

const PaywallModal = ({ open, onOpenChange, context = "generic" }: PaywallModalProps) => {
  const { user, session } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const copy = CONTEXT_COPY[context];

  const handleCheckout = async (type: "starter" | "pro" | "refill") => {
    if (!user || !session) { setShowAuthModal(true); return; }
    setLoading(type);
    trackEvent("checkout_started", { plan: type });
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { type },
      });
      if (error) throw new Error(error.message);
      if (data?.url) window.open(data.url, "_blank");
      else throw new Error("No checkout URL returned");
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-card border-border max-w-2xl p-0 overflow-hidden">
          <DialogHeader className="pt-8 px-6 pb-0">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-cyan/15 border border-cyan/25 flex items-center justify-center">
                <Shield className="w-7 h-7 text-cyan" />
              </div>
            </div>
            <DialogTitle className="text-xl font-bold text-foreground text-center">
              {copy.headline}
            </DialogTitle>
            <p className="text-sm text-muted-foreground text-center mt-2">
              {copy.body}
            </p>
            <p className="text-xs text-muted-foreground/70 text-center mt-1">
              Credits never expire and are added to your account instantly.
            </p>
          </DialogHeader>

          <div className="px-6 pb-8 pt-6">
            {!user && (
              <p className="text-xs text-muted-foreground flex items-center justify-center gap-1 mb-4">
                <User className="w-3.5 h-3.5" /> Sign in required to purchase
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Starter Pack */}
              <div className="p-4 rounded-xl border border-border bg-secondary/30 flex flex-col">
                <h3 className="font-semibold text-foreground text-sm">Starter Pack</h3>
                <p className="text-2xl font-bold text-foreground mt-1">$9.99</p>
                <p className="text-xs text-muted-foreground mb-3">25 credits · ~$0.40/credit</p>
                <div className="space-y-1.5 text-left mb-4 flex-1">
                  <Feature text="25 credits added instantly" />
                  <Feature text="Full forensic analysis" />
                  <Feature text="Response strategy" />
                  <Feature text="Content generation" />
                  <Feature text="Credits never expire" />
                </div>
                <button
                  onClick={() => handleCheckout("starter")}
                  disabled={!!loading}
                  className={cn(
                    "w-full py-2.5 rounded-lg font-semibold text-sm",
                    "bg-secondary border border-border text-foreground",
                    "hover:border-cyan/50 transition-all",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                >
                  {loading === "starter" ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Buy Starter Pack"}
                </button>
              </div>

              {/* Pro Pack */}
              <div className="p-4 rounded-xl border-2 border-cyan/40 bg-gradient-to-b from-cyan/8 to-transparent flex flex-col relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-cyan text-[11px] font-bold text-primary-foreground flex items-center gap-1">
                  <Star className="w-3 h-3" />Best Value
                </div>
                <h3 className="font-semibold text-cyan text-sm">Pro Pack</h3>
                <p className="text-2xl font-bold text-foreground mt-1">$24.99</p>
                <p className="text-xs text-muted-foreground mb-3">80 credits · ~$0.31/credit</p>
                <div className="space-y-1.5 text-left mb-4 flex-1">
                  <Feature text="80 credits added instantly" />
                  <Feature text="Everything in Starter" />
                  <Feature text="Best value per credit" />
                  <Feature text="Ideal for creators" />
                </div>
                <button
                  onClick={() => handleCheckout("pro")}
                  disabled={!!loading}
                  className={cn(
                    "btn-glow w-full py-2.5 rounded-lg font-semibold text-sm",
                    "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                    "hover:scale-[1.02] transition-all",
                    "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  )}
                >
                  {loading === "pro" ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Buy Pro Pack"}
                </button>
              </div>

              {/* 10-Credit Pack */}
              <div className="p-4 rounded-xl border border-border bg-secondary/30 flex flex-col">
                <h3 className="font-semibold text-foreground text-sm">10-Credit Pack</h3>
                <p className="text-2xl font-bold text-foreground mt-1">$4.99</p>
                <p className="text-xs text-muted-foreground mb-3">10 credits · ~$0.50/credit</p>
                <p className="text-xs text-muted-foreground/70 mb-4 flex-1">
                  Buy it on its own or add to your balance.
                </p>
                <button
                  onClick={() => handleCheckout("refill")}
                  disabled={!!loading}
                  className={cn(
                    "w-full py-2.5 rounded-lg font-semibold text-sm",
                    "bg-secondary border border-border text-foreground",
                    "hover:border-cyan/50 transition-all",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                >
                  {loading === "refill" ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Buy 10-Credit Pack"}
                </button>
              </div>
            </div>

            <p className="text-[10px] text-muted-foreground/60 text-center mt-4">
              7-day refund policy · Credits never expire · Added instantly upon purchase
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </>
  );
};

function Feature({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <Check className="w-3 h-3 text-cyan shrink-0" />
      <span>{text}</span>
    </div>
  );
}

export default PaywallModal;
